import React, { useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import * as ICONS from "Components/Icons";
import useTouchDeviceCheck from "lib/effects/useTouchDeviceCheck";
import config from "lib/config";
import * as CONSTANTS from "../constants";
import { Checkbox } from "Components/Base/Inputs/Checkbox";
import { IconButton } from "Components/Base/Inputs/IconButton";
import { Tooltip } from "Components/Base/DataDisplay/Tooltip";
import { Table } from "Components/Base/DataDisplay/Table/Table";
import { TableHead } from "Components/Base/DataDisplay/Table/TableHead";
import { TableCell } from "Components/Base/DataDisplay/Table/TableCell";
import { TableBody } from "Components/Base/DataDisplay/Table/TableBody";
import { TableRow } from "Components/Base/DataDisplay/Table/TableRow";

const DATE_FORMAT =
  config && config.shortDateFormat && config.shortDateFormat.toUpperCase();

const ReminderLabel = (props) => {
  const { label, date } = props;
  const reminderStyle = { minWidth: "85px" };
  return (
    <div className="column" style={reminderStyle}>
      <div>{label}</div>
      <div>{moment(date).format(DATE_FORMAT)}</div>
    </div>
  );
};

ReminderLabel.propTypes = {
  label: PropTypes.string.isRequired,
  date: PropTypes.instanceOf(Date),
};

ReminderLabel.defaultProps = {
  label: [],
  date: {},
};

const TableChecklistRow = (props) => {
  const {
    languageContext,
    actions,
    item,
    columns,
    tableRowActions,
    selectedItems,
  } = props;
  const touchDevice = useTouchDeviceCheck();
  const [displayRow, setDisplay] = useState(!!touchDevice);
  const TableCellCheckedStyle = {
    maxWidth: "35px",
    padding: "10px 0 10px 15px",
  };
  const TableCellDeadlineStyle = { minWidth: "110px" };
  const TableCellActionsStyle = { opacity: displayRow ? "1" : "0" };

  const formatReminder = () => {
    if (item.reminder.reminderType === CONSTANTS.REMINDER_TYPE_NONE) {
      return languageContext("no");
    }

    if (item.reminder.reminderType === CONSTANTS.REMINDER_TYPE_RELATIVE) {
      if (moment(item.reminder.relativeReminderDate).isAfter(new Date())) {
        return (
          <ReminderLabel
            label={languageContext("willbesent")}
            date={item.reminder.relativeReminderDate}
          />
        );
      }
      return (
        <ReminderLabel
          label={languageContext("wassent")}
          date={item.reminder.relativeReminderDate}
        />
      );
    }

    if (moment(item.reminder.reminderDate).isAfter(new Date())) {
      return (
        <ReminderLabel
          label={languageContext("willbesent")}
          date={item.reminder.reminderDate}
        />
      );
    }
    return (
      <ReminderLabel
        label={languageContext("wassent")}
        date={item.reminder.reminderDate}
      />
    );
  };

  const onChkCompletedKeyPress = (event) => {
    switch (event.key) {
      case CONSTANTS.KEY_TAB:
        setDisplay(true);
        break;
      case CONSTANTS.KEY_SPACE:
        event.target.children[0].click();
        break;
      case CONSTANTS.KEY_ENTER:
        event.target.children[0].click();
        break;
    }
  };

  const mouseOverHandler = () => setDisplay(true);
  const mouseOutHandler = () => setDisplay(!!touchDevice);
  const editClickHandler = () => actions.onEditItem(item.checklistItemID);
  const deleteClickHandler = () => actions.onDeleteItem(item.checklistItemID);
  const cloneClickHandler = () => actions.onCloneItem(item.checklistItemID);
  const styleCompletedColumn = { minWidth: "150px" };
  const styleCompletedCheckbox = { minWidth: "40px" };

  return (
    <TableRow onMouseOver={mouseOverHandler} onMouseOut={mouseOutHandler}>
      {columns.includes(CONSTANTS.TABLE_COLUMN_SELECT) && (
        <TableCell style={TableCellCheckedStyle}>
          <Checkbox
            onChange={(e) => actions.onSelectItem(e.target.checked, item)}
            checked={selectedItems.includes(item.checklistItemID)}
          />
        </TableCell>
      )}

      {columns.includes(CONSTANTS.TABLE_COLUMN_TITLE) && (
        <TableCell>
          <span
            className="underline color-1"
            tabIndex="0"
            onClick={editClickHandler}
            onKeyPress={(event) =>
              event.key === CONSTANTS.KEY_ENTER &&
              actions.onEditItem(item.checklistItemID)
            }
          >
            {item.title}
          </span>
        </TableCell>
      )}

      {columns.includes(CONSTANTS.TABLE_COLUMN_DESCRIPTION) && (
        <TableCell>
          <span>{item.description}</span>
        </TableCell>
      )}

      {columns.includes(CONSTANTS.TABLE_COLUMN_DEADLINE) && (
        <TableCell style={TableCellDeadlineStyle}>
          <span className="color-1">
            {item.deadline && item.deadline !== CONSTANTS.ITEM_DEADLINE_BLANK
              ? moment(item.deadline).format(DATE_FORMAT)
              : ""}
          </span>
        </TableCell>
      )}

      {columns.includes(CONSTANTS.TABLE_COLUMN_REMINDER) && (
        <TableCell>
          <span className="color-1">{formatReminder()}</span>
        </TableCell>
      )}
      {columns.includes(CONSTANTS.TABLE_COLUMN_COMPLETED) && (
        <TableCell>
          <div className="flex" style={styleCompletedColumn}>
            <span
              onKeyDown={(event) => onChkCompletedKeyPress(event)}
              style={styleCompletedCheckbox}
            >
              <Checkbox
                onChange={(e) => actions.onCompleteItem(e.target.checked, item)}
                checked={item.completed}
              />
            </span>
            {item.completed ? (
              <div className="column">
                <div>{item.completedByName},</div>
                <div>{moment(item.completedDate).format(DATE_FORMAT)}</div>
              </div>
            ) : (
              ""
            )}
          </div>
        </TableCell>
      )}
      {columns.includes(CONSTANTS.TABLE_COLUMN_CREATEDBY) && (
        <TableCell>
          {item.createdBy ? (
            <div className="column">
              <div>{item.createdBy},</div>
              <div>{moment(item.createdDate).format(DATE_FORMAT)}</div>
            </div>
          ) : (
            ""
          )}
        </TableCell>
      )}
      {columns.includes(CONSTANTS.TABLE_COLUMN_LASTMODIFIED) && (
        <TableCell>
          {item.lastModifiedBy ? (
            <div className="column">
              <div>{item.lastModifiedBy},</div>
              <div>{moment(item.lastModified).format(DATE_FORMAT)}</div>
            </div>
          ) : (
            ""
          )}
        </TableCell>
      )}
      <TableCell>
        <div className="flex flex-end" style={TableCellActionsStyle}>
          {tableRowActions.includes(CONSTANTS.TABLE_ROW_ACTION_EDIT) && (
            <Tooltip title={languageContext("edit")}>
              <div>
                <IconButton
                  icon={<ICONS.EDIT size={22} />}
                  onClick={editClickHandler}
                  ariaLabel={languageContext("edit")}
                />
              </div>
            </Tooltip>
          )}
          {tableRowActions.includes(CONSTANTS.TABLE_ROW_ACTION_DELETE) && (
            <Tooltip title={languageContext("remove")}>
              <div>
                <IconButton
                  icon={<ICONS.REMOVE size={22} />}
                  onClick={deleteClickHandler}
                  ariaLabel={languageContext("remove")}
                />
              </div>
            </Tooltip>
          )}
          {tableRowActions.includes(CONSTANTS.TABLE_ROW_ACTION_COPY) && (
            <Tooltip title={languageContext("tlpcopy")}>
              <div>
                <IconButton
                  icon={<ICONS.COPY size={22} />}
                  onClick={cloneClickHandler}
                  ariaLabel={languageContext("tlpcopy")}
                  onKeyDown={(event) =>
                    event.key === CONSTANTS.KEY_TAB && setDisplay(false)
                  }
                />
              </div>
            </Tooltip>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};

const ChecklistTable = (props) => {
  const { list, columns } = props;
  const { languageContext } = props;
  const TableCellCheckedStyle = {
    maxWidth: "35px",
    padding: "10px 0 10px 15px",
  };

  return (
    list && (
      <div className="maindatatable-wrapper">
        <Table className="maindatatable">
          <TableHead>
            <TableRow>
              {columns.includes(CONSTANTS.TABLE_COLUMN_SELECT) && (
                <TableCell style={TableCellCheckedStyle} />
              )}

              {columns.includes(CONSTANTS.TABLE_COLUMN_TITLE) && (
                <TableCell>{languageContext("title")}</TableCell>
              )}
              {columns.includes(CONSTANTS.TABLE_COLUMN_DESCRIPTION) && (
                <TableCell>{languageContext("description")}</TableCell>
              )}
              {columns.includes(CONSTANTS.TABLE_COLUMN_DEADLINE) && (
                <TableCell>{languageContext("deadline")}</TableCell>
              )}
              {columns.includes(CONSTANTS.TABLE_COLUMN_REMINDER) && (
                <TableCell>{languageContext("reminder")}</TableCell>
              )}
              {columns.includes(CONSTANTS.TABLE_COLUMN_COMPLETED) && (
                <TableCell>{languageContext("completed")}</TableCell>
              )}
              {columns.includes(CONSTANTS.TABLE_COLUMN_CREATEDBY) && (
                <TableCell>{languageContext("Created")}</TableCell>
              )}
              {columns.includes(CONSTANTS.TABLE_COLUMN_LASTMODIFIED) && (
                <TableCell>{languageContext("LastModified")}</TableCell>
              )}
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {list.map((item, index) => (
              <TableChecklistRow key={index} item={item} {...props} />
            ))}
          </TableBody>
        </Table>
      </div>
    )
  );
};

const ChecklistTableAdmin = (props) => {
  const { languageContext, list } = props;
  const visibleColumns = [
    CONSTANTS.TABLE_COLUMN_SELECT,
    CONSTANTS.TABLE_COLUMN_DESCRIPTION,
    CONSTANTS.TABLE_COLUMN_TITLE,
    CONSTANTS.TABLE_COLUMN_DEADLINE,
    CONSTANTS.TABLE_COLUMN_CREATEDBY,
    CONSTANTS.TABLE_COLUMN_LASTMODIFIED,
  ];
  const tableRowActions = [CONSTANTS.TABLE_ROW_ACTION_COPY];

  return list && list.length > 0 ? (
    <ChecklistTable
      columns={visibleColumns}
      tableRowActions={tableRowActions}
      {...props}
    />
  ) : (
    <TableEmpty>{languageContext("emptychecklist")}</TableEmpty>
  );
};

const ChecklistTablePortal = (props) => {
  const { languageContext, list } = props;
  const visibleColumns = [
    CONSTANTS.TABLE_COLUMN_TITLE,
    CONSTANTS.TABLE_COLUMN_DEADLINE,
    CONSTANTS.TABLE_COLUMN_REMINDER,
    CONSTANTS.TABLE_COLUMN_COMPLETED,
  ];

  const tableRowActions = [
    CONSTANTS.TABLE_ROW_ACTION_EDIT,
    CONSTANTS.TABLE_ROW_ACTION_DELETE,
    CONSTANTS.TABLE_ROW_ACTION_COPY,
  ];

  return list && list.length > 0 ? (
    <ChecklistTable
      columns={visibleColumns}
      tableRowActions={tableRowActions}
      {...props}
    />
  ) : (
    <TableEmpty>{languageContext("emptychecklist")}</TableEmpty>
  );
};

const TableEmpty = (props) => {
  return <h4 style={{ textAlign: "center" }}>{props.children}</h4>;
};

TableChecklistRow.propTypes = {
  items: PropTypes.array.isRequired,
  languageContext: PropTypes.func.isRequired,
  actions: PropTypes.object,
  columns: PropTypes.arrayOf(PropTypes.string),
  tableRowActions: PropTypes.arrayOf(PropTypes.string),
};

TableChecklistRow.defaultProps = {
  items: [],
  columns: ["title"],
  tableRowActions: ["edit"],
  actions: {},
  languageContext: () => {},
};

TableEmpty.propTypes = {
  children: PropTypes.element,
};

ChecklistTable.propTypes = {
  list: PropTypes.array.isRequired,
  columns: PropTypes.arrayOf(PropTypes.string),
  tableRowActions: PropTypes.arrayOf(PropTypes.string),
  languageContext: PropTypes.func.isRequired,
  actions: PropTypes.object,
};

const defaultRowActions = ["edit"];
const defaultColumns = ["title", "deadline"];

ChecklistTable.defaultProps = {
  list: [],
  columns: defaultColumns,
  tableRowActions: defaultRowActions,
  actions: {},
  languageContext: () => {},
};

export { ChecklistTableAdmin };
export { ChecklistTablePortal };

export default ChecklistTable;
